/**
 * usePersonalDataImport Hook
 *
 * Purpose: Provides stateful React management for the CSV personal-data import flow.
 *
 * Exposes:
 * - downloadTemplate – fetches categories+fields and triggers the template CSV download.
 * - importFile – parses, validates, and imports a CSV File, returning a CsvImportResult.
 * - exportData – exports existing user personal data to CSV.
 * - downloadErrorReport – triggers download of the error-report CSV for failed rows.
 * - State: isImporting, isExporting, isDownloadingTemplate, importResult, importError.
 */

import { useState, useCallback } from 'react';
import { useAuth } from '@xanectech/auth';
import { createLogger } from '@xanectech/lib';
import {
  PersonalDataImportService,
  type CsvExportResult,
  type CsvImportResult,
  type ImportRowResult,
} from '../services/PersonalDataImportService';

const logger = createLogger('Data - usePersonalDataImport');

export interface UsePersonalDataImportReturn {
  /** Whether an import operation is in progress. */
  isImporting: boolean;
  /** Whether the template download is in progress. */
  isDownloadingTemplate: boolean;
  /** Whether an export operation is in progress. */
  isExporting: boolean;
  /** Result from the most recent importFile call, or null. */
  importResult: CsvImportResult | null;
  /** Error from the most recent operation, or null. */
  importError: string | null;
  /** Download the import template CSV. */
  downloadTemplate: () => Promise<void>;
  /** Parse and import a CSV File, returning the result. */
  importFile: (file: File) => Promise<CsvImportResult | null>;
  /** Export existing personal data to CSV. */
  exportData: () => Promise<CsvExportResult | null>;
  /** Download the error report for failed rows from the last import. */
  downloadErrorReport: (rowResults: ImportRowResult[]) => void;
  /** Reset the hook state (clear result and error). */
  reset: () => void;
}

/**
 * Hook for managing the personal data CSV import flow.
 */
export function usePersonalDataImport(): UsePersonalDataImportReturn {
  const { user } = useAuth();
  const [isImporting, setIsImporting] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [importResult, setImportResult] = useState<CsvImportResult | null>(
    null,
  );
  const [importError, setImportError] = useState<string | null>(null);

  const downloadTemplate = useCallback(async () => {
    if (!user?.id) {
      setImportError('You must be logged in to download the template.');
      return;
    }

    setIsDownloadingTemplate(true);
    setImportError(null);
    try {
      await PersonalDataImportService.downloadTemplate(String(user.id));
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to download template.';
      logger.error('Template download failed', { error: message });
      setImportError(message);
    } finally {
      setIsDownloadingTemplate(false);
    }
  }, [user]);

  const importFile = useCallback(
    async (file: File): Promise<CsvImportResult | null> => {
      if (!user?.id) {
        setImportError('You must be logged in to import data.');
        return null;
      }

      setIsImporting(true);
      setImportResult(null);
      setImportError(null);

      try {
        const csvText = await file.text();

        const { rows, parseErrors } =
          PersonalDataImportService.parseCSV(csvText);

        if (parseErrors.length > 0) {
          const message = parseErrors.join(' ');
          setImportError(message);
          logger.warn('CSV parse errors', { errors: parseErrors });
          return null;
        }

        if (rows.length === 0) {
          setImportError('The CSV file contains no data rows.');
          return null;
        }

        const result = await PersonalDataImportService.importCSV(
          rows,
          String(user.id),
        );
        setImportResult(result);
        logger.info('Import completed', {
          totalRows: result.totalRows,
          importedCount: result.importedCount,
          errorCount: result.errorCount,
        });
        return result;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Import failed unexpectedly.';
        logger.error('Import failed', { error: message });
        setImportError(message);
        return null;
      } finally {
        setIsImporting(false);
      }
    },
    [user],
  );

  const downloadErrorReport = useCallback((rowResults: ImportRowResult[]) => {
    PersonalDataImportService.downloadErrorReport(rowResults);
  }, []);

  const exportData = useCallback(async (): Promise<CsvExportResult | null> => {
    if (!user?.id) {
      setImportError('You must be logged in to export data.');
      return null;
    }

    setIsExporting(true);
    setImportError(null);

    try {
      const result = await PersonalDataImportService.exportCSV(String(user.id));
      logger.info('Export completed', { totalRows: result.totalRows });
      return result;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Export failed unexpectedly.';
      logger.error('Export failed', { error: message });
      setImportError(message);
      return null;
    } finally {
      setIsExporting(false);
    }
  }, [user]);

  const reset = useCallback(() => {
    setImportResult(null);
    setImportError(null);
  }, []);

  return {
    isImporting,
    isDownloadingTemplate,
    isExporting,
    importResult,
    importError,
    downloadTemplate,
    importFile,
    exportData,
    downloadErrorReport,
    reset,
  };
}
